import { useServicesStore } from "./stores/services";
import { useWithdrawalStore } from "./stores/withdrawal";
import { useLocaleStore } from "./stores/locale";
import { useVerticalStore } from "./stores/vertical";
import { useSidebarStore } from "./stores/sidebar";
import { useCurrencyStore } from "./stores/currency";
import { useBalanceStore } from "./stores/balances";
import { useBonusStore } from "./stores/bonus";
import { useSessionStore } from "./stores/sessions";
import { useFilterStore } from "./stores/filters";
import { useStoreUtils } from "./stores/utils";
import { useLivechatStore } from "./stores/livechat";
/**
 * A Composable for AlpineJS stores.
 * @see https://alpinejs.dev/globals/alpine-store
 * Available stores:
 * - sessions: flag for current session state (bet limit, insufficiant balance, etc)
 * - currency: for current currency and formatted outputs
 * - balances: for current user balance (bonus, cash, ...)
 * - bonus: for current user active bonus
 * - withdrawal: for withdrawal states
 * - services: for services providers (like sportsbook)
 * - filters: for filtering content (games, providers, ...)
 * - sidebar: for sidebar state (visible, etc.)
 * - vertical:
 * - locale: for current locale and output, like months
 *
 * @remarks
 * the component that use the store must have a x-data (even empty) to create a context.
 *
 * @example
 * ```js
 * const stores = useStores();
 * stores.getStore('balance').value = 100;
 * ```
 *
 * ```php
 * <div x-data>
 *   <div class="balance" x-text="$store.balance?.text ?? ''">
 * </div>
 * ```
 * @returns {Object} An object containing init and getStore methods, and balance/bonus names getters
 */
export const useStores = () => {
    const stores = {
        sessions: {
            store: useSessionStore(),
            priority: 20,
        },
        currency: {
            store: useCurrencyStore(),
            priority: 30,
        },
        balances: {
            store: useBalanceStore(),
            priority: 40,
        },
        bonus: {
            store: useBonusStore(),
            priority: 50,
        },
        withdrawal: {
            store: useWithdrawalStore(),
            priority: 60,
        },
        services: {
            store: useServicesStore(),
            priority: 70,
        },
        filters: {
            store: useFilterStore(),
            priority: 80,
        },
        sidebar: {
            store: useSidebarStore(),
            priority: 90,
        },
        vertical: {
            store: useVerticalStore(),
            priority: 100,
        },
        locale: {
            store: useLocaleStore(),
            priority: 110,
        },
        livechat: {
            store: useLivechatStore(),
            priority: 120,
        },
    };

    const { getStore } = useStoreUtils();

    /**
     * Initializes all stores and populates them with data from appContext
     * Sets up locale, currency, balances, and bonus information
     * @public
     */
    const init = () => {
        const storeEntries = Object.values(stores);
        storeEntries.sort((a, b) => a.priority - b.priority);

        for (const entry of storeEntries) {
            if (entry.store && typeof entry.store.init === "function") {
                entry.store.init();
            }
        }
    };

    return {
        init,
        getStore,
        /**
         * @readonly
         * @returns {string[]} List of available balance store names
         */
        get balanceNames() {
            return stores.balances.store.balanceNames;
        },
        /**
         * @readonly
         * @returns {string[]} List of available bonus store names
         */
        get bonusNames() {
            return stores.bonus.store.bonusNames;
        },
    };
};
